import { Moment } from 'moment';
import queryString from 'query-string';
import {
  IRestApiService,
  IStudioApiCall,
  NewStudio,
  RepeatableEvent,
  Room,
  ScheduledEvent,
  Studio,
  StudioClient,
  UserData,
  WorkingHours,
} from '../types';
import { StudioTracker } from '../services/analytics/apiTrackers';

export class StudioApiCall implements IStudioApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getStudioById(id: string): Promise<Studio> {
    return this.restApiService.get('studios/' + id);
  }

  getPublicStudioByOwnerId(ownerId: string): Promise<Studio> {
    return this.restApiService.get('studios/public/' + ownerId);
  }

  getOtherSuggestions(id: string): Promise<any[]> {
    return this.restApiService.get(`studios/${id}/suggestions`);
  }

  getStudioByIds(ids: string[]): Promise<Studio[]> {
    return this.restApiService.post('studios/ids', { ids: ids });
  }

  deleteStudioById(id: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .delete('studios/' + id)
      .then((result) => {
        StudioTracker.trackDeleteStudio({
          studioId: id,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackDeleteStudio({
          studioId: id,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteBackground(id: string): Promise<any> {
    return this.restApiService.delete('studios/' + id + '/background');
  }

  deleteAvatar(id: string): Promise<any> {
    return this.restApiService.delete('studios/' + id + '/avatar');
  }

  deleteProfile(id: string): Promise<any> {
    return this.restApiService.delete('studios/' + id + '/profile');
  }

  inviteOwner(id: string): Promise<any> {
    return this.restApiService.post('invite/' + id);
  }

  getInvite(token: string): Promise<any> {
    return this.restApiService.get('invite/' + token);
  }

  createUserFromInvite(token: string, userData: UserData): Promise<any> {
    return this.restApiService.put('invite/' + token, userData);
  }

  getCategories(): Promise<any[]> {
    let url = 'studios/categories';
    return this.restApiService.get(url);
  }

  getCategoriesAgg(params: any): Promise<any[]> {
    let url = 'studios/categoriesAgg';
    return this.restApiService.post(url, params);
  }

  getSchedule(id: string): Promise<any[]> {
    return this.restApiService.get(`studios/${id}/schedule`);
  }

  getPublicSchedule(id: string): Promise<any[]> {
    return this.restApiService.get(`studios/${id}/public-schedule`);
  }

  getPublicScheduleOnTheWeek(id: string, from: Moment, to: Moment): Promise<any[]> {
    return this.restApiService.get(
      `studios/${id}/public-schedule/calendar?from=${from.format('DD-MM-YYYY')}&to=${to.format('DD-MM-YYYY')}`
    );
  }

  createStudio(studio: Studio): Promise<Studio> {
    const startTime = Date.now();

    return this.restApiService
      .post('studios', { institution: studio })
      .then((result) => {
        StudioTracker.trackCreateStudio({
          studio,
          success: true,
          duration: Date.now() - startTime,
          result,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackCreateStudio({
          studio,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addFreeTextStudio(newStudio: NewStudio): Promise<Studio> {
    return this.restApiService.post('studios/free-text', newStudio);
  }

  updateStudio(studio: Studio, workingHours?: WorkingHours): Promise<Studio> {
    const startTime = Date.now();
    return this.restApiService
      .put('studios', {
        institution: studio,
        workingHours: workingHours,
      })
      .then((result) => {
        StudioTracker.trackUpdateStudio({
          studio,
          workingHours,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackUpdateStudio({
          studio,
          workingHours,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  trueUpdateStudio(id: string, studio: Studio, workingHours?: WorkingHours): Promise<Studio> {
    let body: any = { institution: studio };
    if (workingHours) {
      body.workingHours = workingHours;
    }
    return this.restApiService.put('studios/' + id, body);
  }

  getStudioClassesByDate(id: string, date: string, client?: StudioClient, useCurrentUser?: boolean): Promise<any[]> {
    let url = `studios/${id}/class/date/${date}`;
    if (client) {
      url = url + `?studioClientId=${client._id}`;
    } else if (useCurrentUser) {
      url = url + `?useCurrentUser=${useCurrentUser}`;
    }
    return this.restApiService.get(url);
  }

  getStudioClassesByRange(id: string, client: StudioClient, from: Moment, to: Moment): Promise<any[]> {
    let url = `studios/${id}/class-from-range?studioClientId=${client._id}&from=${from.format(
      'DD-MM-YYYY'
    )}&to=${to.format('DD-MM-YYYY')}`;

    return this.restApiService.get(url);
  }

  getStudioByDomainName(domainName: string): Promise<Studio> {
    return this.restApiService.get('studios/d/' + domainName);
  }

  getStudioWithClientRegistration(
    id: string,
    repeatableClassId: string,
    from: string,
    to: string,
    studioClientId?: string
  ): Promise<any> {
    let url = `studios/${id}/class/${repeatableClassId}?from=${from}&to=${to}`;
    if (studioClientId) {
      url += `&studioClientId=${studioClientId}`;
    }
    return this.restApiService.get(url);
  }

  getScheduledEvent(studioId: string, eventId: string): Promise<ScheduledEvent> {
    return this.restApiService.get(`schedules/${studioId}/${eventId}`);
  }

  getAllCourses(ownerId: string, studioId: string): Promise<any[]> {
    return this.restApiService.get(`schedules/all-courses/${ownerId}/${studioId}`);
  }

  getAllDeletedCourses(ownerId: string, studioId: string): Promise<any[]> {
    return this.restApiService.get(`schedules/${ownerId}/${studioId}/deleted-schedule`);
  }

  getDeletedCourse(studioId: string, courseId: string): Promise<any> {
    return this.restApiService.get(`schedules/deleted-course/${studioId}/${courseId}`);
  }

  updateScheduledEvent(studioId: string, eventId: string, scheduledEvent: ScheduledEvent): Promise<ScheduledEvent> {
    return this.restApiService.put(`schedules/${studioId}/${eventId}`, scheduledEvent);
  }

  moveToCourseScheduledEvent(
    studioId: string,
    courseId: string,
    eventId: string,
    scheduledEvent: ScheduledEvent
  ): Promise<ScheduledEvent> {
    return this.restApiService.put(`schedules/${studioId}/to-course/${courseId}/${eventId}`, scheduledEvent);
  }

  updateRepeatableEvent(
    studioId: string,
    repeatableEventId: string,
    repeatableEvent: RepeatableEvent,
    updateEventsFields: any
  ): Promise<RepeatableEvent> {
    const startTime = Date.now();

    return this.restApiService
      .put(`schedules/${studioId}/repeatable-event/${repeatableEventId}`, {
        repeatableEvent: repeatableEvent,
        updateEventsFields: updateEventsFields,
      })
      .then((result) => {
        StudioTracker.trackUpdateRepeatableEvent({
          studioId,
          repeatableEventId,
          repeatableEvent,
          updateEventsFields,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackUpdateRepeatableEvent({
          studioId,
          repeatableEventId,
          repeatableEvent,
          updateEventsFields,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addRepeatableEvent(studioId: string, repeatableEvent: RepeatableEvent): Promise<RepeatableEvent> {
    const startTime = Date.now();

    return this.restApiService
      .post(`schedules/${studioId}/repeatable-event`, repeatableEvent)
      .then((result) => {
        StudioTracker.trackAddRepeatableEvent({
          studioId,
          repeatableEvent,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackAddRepeatableEvent({
          studioId,
          repeatableEvent,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  restoreCourse(studioId: string, courseId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .put(`schedules/restore-course/${studioId}/${courseId}`)
      .then((result) => {
        StudioTracker.trackRestoreCourse({
          studioId,
          courseId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackRestoreCourse({
          studioId,
          courseId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addRoom(studioId: string, room: Room): Promise<Room> {
    const startTime = Date.now();

    return this.restApiService
      .post(`schedules/${studioId}/room`, room)
      .then((result) => {
        StudioTracker.trackAddRoom({
          studioId,
          room,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackAddRoom({
          studioId,
          room,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateRoom(studioId: string, room: Room): Promise<Room> {
    const startTime = Date.now();

    return this.restApiService
      .put(`schedules/${studioId}/room/${room._id}`, room)
      .then((result) => {
        StudioTracker.trackUpdateRoom({
          studioId,
          room,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackUpdateRoom({
          studioId,
          room,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteRoom(studioId: string, roomId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .delete(`schedules/${studioId}/room/${roomId}`)
      .then((result) => {
        StudioTracker.trackDeleteRoom({
          studioId,
          roomId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackDeleteRoom({
          studioId,
          roomId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addScheduledEvent(
    studioId: string,
    repeatableEventId: string,
    scheduledEvent: ScheduledEvent,
    newSelectedDays?: Array<number | { day: number; start: string }>
  ): Promise<ScheduledEvent> {
    const startTime = Date.now();

    return this.restApiService
      .post(`schedules/${studioId}/${repeatableEventId}`, {
        scheduledEvent: scheduledEvent,
        newSelectedDays: newSelectedDays,
      })
      .then((result) => {
        StudioTracker.trackAddScheduledEvent({
          studioId,
          repeatableEventId,
          scheduledEvent,
          newSelectedDays,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackAddScheduledEvent({
          studioId,
          repeatableEventId,
          scheduledEvent,
          newSelectedDays,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteScheduledEvent(studioId: string, eventId: string, groupId?: string, deleteFuture?: boolean): Promise<any> {
    const startTime = Date.now();

    // schedule.events entries are recurring-slot templates (day-of-week + time, no real
    // date), so the server can't derive which specific date's Attendance record to clean
    // up on its own; pass along the exact groupId of the occurrence being deleted.
    // deleteFuture additionally cascades Attendance cleanup to every future occurrence of
    // this same eventId/slot, without touching other days or the parent course.
    const params: string[] = [];
    if (groupId) params.push(`groupId=${encodeURIComponent(groupId)}`);
    if (deleteFuture) params.push('deleteFuture=true');
    const query = params.length ? `?${params.join('&')}` : '';
    return this.restApiService
      .delete(`schedules/${studioId}/${eventId}${query}`)
      .then((result) => {
        StudioTracker.trackDeleteScheduledEvent({
          studioId,
          eventId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackDeleteScheduledEvent({
          studioId,
          eventId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteRepeatableEvent(studioId: string, repeatableEventId: string): Promise<any> {
    const startTime = Date.now();

    return this.restApiService
      .delete(`schedules/${studioId}/repeatable-event/${repeatableEventId}`)
      .then((result) => {
        StudioTracker.trackDeleteRepeatableEvent({
          studioId,
          repeatableEventId,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        StudioTracker.trackDeleteRepeatableEvent({
          studioId,
          repeatableEventId,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  uploadAvatar(file: File): Promise<any> {
    return this.restApiService.file(`images/avatar`, file);
  }

  uploadBackground(file: File): Promise<any> {
    return this.restApiService.file(`images/`, file);
  }

  uploadGallery(file: File): Promise<any> {
    return this.restApiService.file(`images/gallery`, file);
  }

  uploadProfile(file: File): Promise<any> {
    return this.restApiService.file(`images/profile`, file);
  }

  setUnavailable(ownerId: string, id: string, data: any): Promise<any> {
    let url = `studios/${ownerId}/${id}/unavailable`;
    return this.restApiService.put(url, data);
  }

  resetUnavailableSlot(ownerId: string, id: string, data: any): Promise<any> {
    let url = `studios/${ownerId}/${id}/available`;
    return this.restApiService.put(url, data);
  }

  getClientEvents(ownerId: string, clientTags: string[], clientId: string): Promise<any[]> {
    return this.restApiService.get(
      `schedules/get-client-events/${ownerId}/${clientId}?${clientTags && queryString.stringify({ clientTags })}`
    );
  }

  validateRooms(ownerId: string, studioId: string, params: any): Promise<any> {
    return this.restApiService.post(`schedules/${ownerId}/validate-room/${studioId}`, params);
  }

  validateTeachers(ownerId: string, studioId: string, params: any): Promise<any> {
    return this.restApiService.post(`schedules/${ownerId}/validate-teachers/${studioId}`, params);
  }

  getTeacherEvents(ownerId: string, teacherId: string, day: string, from?: Moment, to?: Moment): Promise<any[]> {
    let query = '';
    if (from && to) {
      query = `?from=${from.toISOString()}&to=${to.toISOString()}`;
    }
    return this.restApiService.get(`schedules/get-teacher-events/${ownerId}/${teacherId}/${day}${query}`);
  }

  predictRatePlan(
    ownerId: string,
    clientTags: string[],
    billingTags: string[],
    from: string,
    to: string,
    limit: number,
    clientId: string
  ): Promise<any> {
    return this.restApiService.post(`schedules/${ownerId}/predict-rate-plan`, {
      clientTags: clientTags,
      billingTags: billingTags,
      clientId: clientId,
      from: from,
      to: to,
      limit: limit,
    });
  }

  predictCourse(ownerId: string, studioId: string, courseId: string, from: string, to: string): Promise<any> {
    return this.restApiService.post(`schedules/${ownerId}/predict-course/`, {
      studioId: studioId,
      courseId: courseId,
      from: from,
      to: to,
    });
  }

  getStudioLTV(params: LTVParams): Promise<any> {
    const url = `studios/ltv/${params.userId}?from=${params.startDate}&to=${params.endDate}&isActive=${params.isActive}&isRemoved=${params.isRemoved}`;
    return this.restApiService.get(url);
  }
}

export default StudioApiCall;
